'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function TakeHomePayCalculator() {
  const t = useTranslations("tools.takeHomePayCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    annualIncome: 80000,
    payFrequency: 26,
    filingStatus: 'single',
    stateTaxRate: 0,
    children: 0,
    otherDependents: 0,
    pretaxDeductions: 6000,
    otherIncome: 0,
    income2: 0,
    income3: 0,
    hasAdditional: false,
    isSelfEmployed: false,
  });

  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const taxBrackets2025: any = {
    single: [
      { limit: 11925, rate: 0.10 },
      { limit: 48475, rate: 0.12 },
      { limit: 103350, rate: 0.22 },
      { limit: 197300, rate: 0.24 },
      { limit: 250525, rate: 0.32 },
      { limit: 626350, rate: 0.35 },
      { limit: Infinity, rate: 0.37 }
    ],
    'married-joint': [
      { limit: 23850, rate: 0.10 },
      { limit: 96950, rate: 0.12 },
      { limit: 206700, rate: 0.22 },
      { limit: 394600, rate: 0.24 },
      { limit: 501050, rate: 0.32 },
      { limit: 751600, rate: 0.35 },
      { limit: Infinity, rate: 0.37 }
    ],
    'married-separate': [
      { limit: 11925, rate: 0.10 },
      { limit: 48475, rate: 0.12 },
      { limit: 103350, rate: 0.22 },
      { limit: 197300, rate: 0.24 },
      { limit: 250525, rate: 0.32 },
      { limit: 375800, rate: 0.35 },
      { limit: Infinity, rate: 0.37 }
    ],
    head: [
      { limit: 17000, rate: 0.10 },
      { limit: 64850, rate: 0.12 },
      { limit: 103350, rate: 0.22 },
      { limit: 197300, rate: 0.24 },
      { limit: 250500, rate: 0.32 },
      { limit: 626350, rate: 0.35 },
      { limit: Infinity, rate: 0.37 }
    ]
  };

  const standardDeductions2025: any = {
    single: 15000,
    'married-joint': 30000,
    'married-separate': 15000,
    head: 22500
  };

  const calculateFederalTax = (taxableIncome: number, filingStatus: string) => {
    const brackets = taxBrackets2025[filingStatus];
    let tax = 0;
    let previousLimit = 0;

    for (let bracket of brackets) {
      if (taxableIncome > previousLimit) {
        const taxableInBracket = Math.min(taxableIncome, bracket.limit) - previousLimit;
        tax += taxableInBracket * bracket.rate;
        previousLimit = bracket.limit;
      } else {
        break;
      }
    }

    return tax;
  };

  const formatCurrency = (amount: number) => {
    return '$' + Math.round(amount).toLocaleString('en-US');
  };

  const formatPercent = (rate: number) => {
    return (rate * 100).toFixed(1) + '%';
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const totalJobIncome = inputs.annualIncome + inputs.income2 + inputs.income3;
    const totalIncome = totalJobIncome + inputs.otherIncome;

    const adjustedGrossIncome = totalIncome - inputs.pretaxDeductions;
    const standardDeduction = standardDeductions2025[inputs.filingStatus];
    const taxableIncome = Math.max(0, adjustedGrossIncome - standardDeduction);

    const childTaxCredit = inputs.children * 2000;
    const otherDependentCredit = inputs.otherDependents * 500;
    const totalCredits = childTaxCredit + otherDependentCredit;

    let federalTax = calculateFederalTax(taxableIncome, inputs.filingStatus);
    federalTax = Math.max(0, federalTax - totalCredits);

    const socialSecurityWageBase = 176100;
    const socialSecurityTaxableWages = Math.min(totalJobIncome, socialSecurityWageBase);
    let socialSecurityTax = socialSecurityTaxableWages * 0.062;
    let medicareTax = totalJobIncome * 0.0145;

    let selfEmploymentTax = 0;
    if (inputs.isSelfEmployed) {
      const selfEmploymentIncome = totalJobIncome * 0.9235;
      const seTaxableWages = Math.min(selfEmploymentIncome, socialSecurityWageBase);
      selfEmploymentTax = (seTaxableWages * 0.124) + (selfEmploymentIncome * 0.029);
      socialSecurityTax = 0;
      medicareTax = 0;
    }

    const stateTax = adjustedGrossIncome * (inputs.stateTaxRate / 100);

    const totalTaxes = federalTax + socialSecurityTax + medicareTax + stateTax + selfEmploymentTax;
    const annualTakeHome = totalIncome - totalTaxes - inputs.pretaxDeductions;
    const takeHomePerPaycheck = annualTakeHome / inputs.payFrequency;
    const grossPerPaycheck = inputs.annualIncome / inputs.payFrequency;

    const effectiveTaxRate = totalIncome > 0 ? totalTaxes / totalIncome : 0;

    const frequencyText: any = {
      '52': t('perWeek'),
      '26': t('perPaycheckBiweekly'),
      '24': t('perPaycheckSemimonthly'),
      '12': t('perMonth'),
      '4': t('perQuarter'),
      '1': t('perYear')
    };

    setResults({
      takeHomePerPaycheck,
      grossPerPaycheck,
      totalTaxes: totalTaxes / inputs.payFrequency,
      totalDeductions: inputs.pretaxDeductions / inputs.payFrequency,
      effectiveTaxRate,
      federalTax: federalTax / inputs.payFrequency,
      socialSecurityTax: socialSecurityTax / inputs.payFrequency,
      medicareTax: medicareTax / inputs.payFrequency,
      stateTax: stateTax / inputs.payFrequency,
      selfEmploymentTax: selfEmploymentTax / inputs.payFrequency,
      frequencyText: frequencyText[inputs.payFrequency.toString()],
      isSelfEmployed: inputs.isSelfEmployed,
    });
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      maxWidth: '100%',
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden' as const,
    },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const,
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderTitleStyle = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formRowStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const formGroupStyle = { marginBottom: '1.5rem' };

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#1e293b' },
    { fontSize: '0.9rem' },
    { fontSize: '0.925rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem' },
    { padding: '0.8rem' },
    { padding: '0.875rem' }
  );

  const selectStyle = { ...inputStyle };

  const inputWithUnitStyle = { position: 'relative' as const };

  const unitStyle = {
    position: 'absolute' as const,
    left: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontWeight: 500,
  };

  const unitSuffixStyle = getResponsiveStyle(
    {
      position: 'absolute' as const,
      right: '0.75rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#64748b',
    },
    { fontSize: '0.85rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' }
  );

  const inputWithPaddingStyle = { ...inputStyle, paddingLeft: '2rem' };

  const radioGroupStyle = getResponsiveStyle(
    { display: 'flex', marginTop: '0.5rem' },
    { gap: '1rem', flexDirection: 'column' as const },
    { gap: '1.5rem', flexDirection: 'row' as const },
    { gap: '1.5rem', flexDirection: 'row' as const }
  );

  const radioLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontWeight: 500,
    cursor: 'pointer',
  };

  const additionalIncomeStyle = {
    display: inputs.hasAdditional ? 'block' : 'none',
    background: '#f8fafc',
    padding: '1.5rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
  };

  const helpTextStyle = getResponsiveStyle(
    { color: '#64748b', marginTop: '0.25rem' },
    { fontSize: '0.8rem' },
    { fontSize: '0.825rem' },
    { fontSize: '0.85rem' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem',
    },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1.05rem', fontSize: '1.05rem' },
    { padding: '1.1rem', fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      display: results ? 'block' : 'none',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderTop: '3px solid #10b981',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: '#065f46', fontWeight: 600 },
    { fontSize: '1.35rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const resultMainStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultLabelStyle = getResponsiveStyle(
    { display: 'block', color: '#64748b', marginBottom: '0.5rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.975rem' },
    { fontSize: '1rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 700, color: '#10b981' },
    { fontSize: '2rem' },
    { fontSize: '2.25rem' },
    { fontSize: '2.5rem' }
  );

  const resultFrequencyStyle = getResponsiveStyle(
    { color: '#64748b', marginTop: '0.25rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.975rem' },
    { fontSize: '1rem' }
  );

  const resultBreakdownStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginTop: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }
  );

  const resultItemStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.5rem',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    },
    { padding: '1rem' },
    { padding: '1.125rem' },
    { padding: '1.25rem' }
  );

  const resultItemLabelStyle = getResponsiveStyle(
    { display: 'block', color: '#64748b', marginBottom: '0.5rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' }
  );

  const resultItemValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#1e293b' },
    { fontSize: '1.35rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const taxBreakdownStyle = getResponsiveStyle(
    {
      marginTop: '1.5rem',
      background: 'white',
      borderRadius: '0.75rem',
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const taxBreakdownH4Style = getResponsiveStyle(
    { margin: '0 0 1rem 0', color: '#1e293b', fontWeight: 600 },
    { fontSize: '1.05rem' },
    { fontSize: '1.075rem' },
    { fontSize: '1.1rem' }
  );

  const taxItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.75rem 0',
    borderBottom: '1px solid #e2e8f0',
  };

  const taxLabelStyle = { color: '#64748b' };
  const taxValueStyle = { fontWeight: 600, color: '#1e293b' };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem',
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const insightCardStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(255, 240, 245, 0.8) 0%, rgba(255, 228, 230, 0.8) 100%)',
      border: '1px solid rgba(255, 90, 112, 0.2)',
      borderRadius: '0.75rem',
      marginBottom: '1rem',
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const insightTitleStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.primary, marginBottom: '0.5rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' },
    { fontSize: '1.2rem' }
  );

  const faqItemStyle = { marginBottom: '1.5rem' };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    transition: 'color 0.3s ease',
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderTitleStyle}>{t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="annual-income">{t('annualSalary')}</label>
                <div style={inputWithUnitStyle}>
                  <span style={unitStyle}>$</span>
                  <input
                    type="number"
                    id="annual-income"
                    style={inputWithPaddingStyle}
                    value={inputs.annualIncome}
                    onChange={(e) => setInputs({...inputs, annualIncome: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="1000"
                    required
                  />
                  <span style={unitSuffixStyle}>{t('perYear')}</span>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="pay-frequency">{t('payFrequency')}</label>
                <select
                  id="pay-frequency"
                  style={selectStyle}
                  value={inputs.payFrequency}
                  onChange={(e) => setInputs({...inputs, payFrequency: parseInt(e.target.value)})}
                >
                  <option value="52">{t('weekly')}</option>
                  <option value="26">{t('biweekly')}</option>
                  <option value="24">{t('semimonthly')}</option>
                  <option value="12">{t('monthly')}</option>
                  <option value="4">{t('quarterly')}</option>
                  <option value="1">{t('annually')}</option>
                </select>
              </div>
            </div>

            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="filing-status">{t('filingStatus')}</label>
                <select
                  id="filing-status"
                  style={selectStyle}
                  value={inputs.filingStatus}
                  onChange={(e) => setInputs({...inputs, filingStatus: e.target.value})}
                >
                  <option value="single">{t('single')}</option>
                  <option value="married-joint">{t('marriedJoint')}</option>
                  <option value="married-separate">{t('marriedSeparate')}</option>
                  <option value="head">{t('headOfHousehold')}</option>
                </select>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="state-tax">{t('stateTaxRate')}</label>
                <div style={inputWithUnitStyle}>
                  <input
                    type="number"
                    id="state-tax"
                    style={inputStyle}
                    value={inputs.stateTaxRate}
                    onChange={(e) => setInputs({...inputs, stateTaxRate: parseFloat(e.target.value) || 0})}
                    min="0"
                    max="15"
                    step="0.1"
                  />
                  <span style={unitSuffixStyle}>%</span>
                </div>
              </div>
            </div>

            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="children">{t('childrenUnder17')}</label>
                <input
                  type="number"
                  id="children"
                  style={inputStyle}
                  value={inputs.children}
                  onChange={(e) => setInputs({...inputs, children: parseInt(e.target.value) || 0})}
                  min="0"
                  step="1"
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="other-dependents">{t('otherDependents')}</label>
                <input
                  type="number"
                  id="other-dependents"
                  style={inputStyle}
                  value={inputs.otherDependents}
                  onChange={(e) => setInputs({...inputs, otherDependents: parseInt(e.target.value) || 0})}
                  min="0"
                  step="1"
                />
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="pretax-deductions">{t('pretaxDeductions')}</label>
              <div style={inputWithUnitStyle}>
                <span style={unitStyle}>$</span>
                <input
                  type="number"
                  id="pretax-deductions"
                  style={inputWithPaddingStyle}
                  value={inputs.pretaxDeductions}
                  onChange={(e) => setInputs({...inputs, pretaxDeductions: parseFloat(e.target.value) || 0})}
                  min="0"
                  step="500"
                />
                <span style={unitSuffixStyle}>{t('perYear')}</span>
              </div>
              <div style={helpTextStyle}>{t('pretaxDeductionsHelp')}</div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="other-income">{t('otherIncome')}</label>
              <div style={inputWithUnitStyle}>
                <span style={unitStyle}>$</span>
                <input
                  type="number"
                  id="other-income"
                  style={inputWithPaddingStyle}
                  value={inputs.otherIncome}
                  onChange={(e) => setInputs({...inputs, otherIncome: parseFloat(e.target.value) || 0})}
                  min="0"
                  step="1000"
                />
                <span style={unitSuffixStyle}>{t('perYear')}</span>
              </div>
              <div style={helpTextStyle}>{t('otherIncomeHelp')}</div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>{t('additionalJobs')}</label>
              <div style={radioGroupStyle}>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    name="has-additional"
                    checked={!inputs.hasAdditional}
                    onChange={() => setInputs({...inputs, hasAdditional: false})}
                  />
                  {t('no')}
                </label>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    name="has-additional"
                    checked={inputs.hasAdditional}
                    onChange={() => setInputs({...inputs, hasAdditional: true})}
                  />
                  {t('yes')}
                </label>
              </div>

              <div style={additionalIncomeStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="income-2">{t('secondIncome')}</label>
                  <div style={inputWithUnitStyle}>
                    <span style={unitStyle}>$</span>
                    <input
                      type="number"
                      id="income-2"
                      style={inputWithPaddingStyle}
                      value={inputs.income2}
                      onChange={(e) => setInputs({...inputs, income2: parseFloat(e.target.value) || 0})}
                      min="0"
                      step="1000"
                    />
                    <span style={unitSuffixStyle}>{t('perYear')}</span>
                  </div>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="income-3">{t('thirdIncome')}</label>
                  <div style={inputWithUnitStyle}>
                    <span style={unitStyle}>$</span>
                    <input
                      type="number"
                      id="income-3"
                      style={inputWithPaddingStyle}
                      value={inputs.income3}
                      onChange={(e) => setInputs({...inputs, income3: parseFloat(e.target.value) || 0})}
                      min="0"
                      step="1000"
                    />
                    <span style={unitSuffixStyle}>{t('perYear')}</span>
                  </div>
                </div>
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>{t('selfEmployed')}</label>
              <div style={radioGroupStyle}>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    name="self-employed"
                    checked={!inputs.isSelfEmployed}
                    onChange={() => setInputs({...inputs, isSelfEmployed: false})}
                  />
                  {t('no')}
                </label>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    name="self-employed"
                    checked={inputs.isSelfEmployed}
                    onChange={() => setInputs({...inputs, isSelfEmployed: true})}
                  />
                  {t('yes')}
                </label>
              </div>
            </div>

            <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>{t('resultsTitle')}</h3>

              <div style={resultMainStyle}>
                <span style={resultLabelStyle}>{t('yourTakeHomePay')}</span>
                <span style={resultValueStyle}>{formatCurrency(results.takeHomePerPaycheck)}</span>
                <div style={resultFrequencyStyle}>{results.frequencyText}</div>
              </div>

              <div style={resultBreakdownStyle}>
                <div style={resultItemStyle}>
                  <span style={resultItemLabelStyle}>{t('grossPay')}</span>
                  <span style={resultItemValueStyle}>{formatCurrency(results.grossPerPaycheck)}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultItemLabelStyle}>{t('totalTaxes')}</span>
                  <span style={resultItemValueStyle}>{formatCurrency(results.totalTaxes)}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultItemLabelStyle}>{t('totalDeductions')}</span>
                  <span style={resultItemValueStyle}>{formatCurrency(results.totalDeductions)}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultItemLabelStyle}>{t('effectiveTaxRate')}</span>
                  <span style={resultItemValueStyle}>{formatPercent(results.effectiveTaxRate)}</span>
                </div>
              </div>

              <div style={taxBreakdownStyle}>
                <h4 style={taxBreakdownH4Style}>{t('taxBreakdown')}</h4>
                <div style={taxItemStyle}>
                  <span style={taxLabelStyle}>{t('federalIncomeTax')}</span>
                  <span style={taxValueStyle}>{formatCurrency(results.federalTax)}</span>
                </div>
                <div style={taxItemStyle}>
                  <span style={taxLabelStyle}>{t('socialSecurity')}</span>
                  <span style={taxValueStyle}>{formatCurrency(results.socialSecurityTax)}</span>
                </div>
                <div style={taxItemStyle}>
                  <span style={taxLabelStyle}>{t('medicare')}</span>
                  <span style={taxValueStyle}>{formatCurrency(results.medicareTax)}</span>
                </div>
                <div style={taxItemStyle}>
                  <span style={taxLabelStyle}>{t('stateIncomeTax')}</span>
                  <span style={taxValueStyle}>{formatCurrency(results.stateTax)}</span>
                </div>
                {results.isSelfEmployed && (
                  <div style={taxItemStyle}>
                    <span style={taxLabelStyle}>{t('selfEmploymentTax')}</span>
                    <span style={taxValueStyle}>{formatCurrency(results.selfEmploymentTax)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introPara1')}</p>
        <p style={paragraphStyle}>{t('introPara2')}</p>
        <p style={paragraphStyle}>{t('introPara3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToStep1')}</li>
          <li style={listItemStyle}>{t('howToStep2')}</li>
          <li style={listItemStyle}>{t('howToStep3')}</li>
          <li style={listItemStyle}>{t('howToStep4')}</li>
          <li style={listItemStyle}>{t('howToStep5')}</li>
          <li style={listItemStyle}>{t('howToStep6')}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>

        <div style={insightCardStyle}>
          <h3 style={insightTitleStyle}>{t('insight1Title')}</h3>
          <p style={paragraphStyle}>{t('insight1Text')}</p>
        </div>

        <div style={insightCardStyle}>
          <h3 style={insightTitleStyle}>{t('insight2Title')}</h3>
          <p style={paragraphStyle}>{t('insight2Text')}</p>
        </div>

        <div style={insightCardStyle}>
          <h3 style={insightTitleStyle}>{t('insight3Title')}</h3>
          <p style={paragraphStyle}>{t('insight3Text')}</p>
        </div>

        <div style={insightCardStyle}>
          <h3 style={insightTitleStyle}>{t('insight4Title')}</h3>
          <p style={paragraphStyle}>{t('insight4Text')}</p>
        </div>

        <div style={insightCardStyle}>
          <h3 style={insightTitleStyle}>{t('insight5Title')}</h3>
          <p style={paragraphStyle}>{t('insight5Text')}</p>
        </div>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem'}}>{t('detail1Title')}</h3>
        <p style={paragraphStyle}>{t('detail1Text')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem'}}>{t('detail2Title')}</h3>
        <p style={paragraphStyle}>{t('detail2Text')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem'}}>{t('detail3Title')}</h3>
        <p style={paragraphStyle}>{t('detail3Text')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem'}}>{t('detail4Title')}</h3>
        <p style={paragraphStyle}>{t('detail4Text')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem'}}>{t('detail5Title')}</h3>
        <p style={paragraphStyle}>{t('detail5Text')}</p>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq6Question')}</h3>
          <p style={paragraphStyle}>{t('faq6Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://www.calculator.net/take-home-pay-calculator.html" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.patriotsoftware.com/blog/payroll/understanding-take-home-pay/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.adp.com/resources/tools/calculators.aspx" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.irs.gov/individuals/tax-withholding-estimator" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref4')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.adp.com/resources/tools/calculators/salary-paycheck-calculator.aspx" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref5')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://quickbooks.intuit.com/payroll/paycheck-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref6')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}